<template>
    <BaseModal :title="title" @close="handleModal" :visible="true" size="medium">
        <div class="phonetic__symbol__guide__popup updated__popup">
            <!-- Search Input -->
            <div class="keyboard__input__field">
                <input type="text" v-model="inputTag" placeholder="Search symbol or sound..." />
            </div>

            <!-- Table Wrapper -->
            <div class="phonetic__symbol__guide__table">
                <div class="phonetic__table__body">
                    <table>
                        <thead>
                            <tr>
                                <th class="phonetic__head__item" style="width:173px">
                                    <span>English Phonemes</span>
                                </th>
                                <th class="phonetic__head__item" style="width:180px">
                                    <span>Phonetic Symbol</span>
                                </th>
                                <th class="phonetic__head__item" style="width:180px">
                                    <span>Sounds Like</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- Loop through grouped and filtered phonetic rows -->
                            <template
                                v-for="(rows, phoneme, groupIndex) in filteredPhoneticGroups"
                                :key="groupIndex"
                            >
                                <template v-for="(row, index) in rows" :key="index">
                                    <tr>
                                        <!-- Only display phoneme once with rowspan -->
                                        <template v-if="index === 0">
                                            <td :rowspan="rows.length" class="tab__head__item" style="width:173px;">
                                                <div class="table__td">
                                                    <span class="text">{{ phoneme }}</span>
                                                </div>
                                            </td>
                                        </template>

                                        <!-- Symbol column -->
                                        <td style="width:180px">
                                            <div class="table__td">
                                                <span class="label__text">{{ row.symbol }}</span>
                                            </div>
                                        </td>

                                        <!-- Sound column -->
                                        <td style="width:180px">
                                            <div class="table__td">
                                                <span class="text">{{ row.sound }}</span>
                                                <a href="javascript:void(0);" @click.prevent="row.audio && playSound(row.audio, `${groupIndex}_${index}`)" v-if="false">
                                                    <span class="material-symbols-outlined" :class="{'active': `${groupIndex}_${index}` === currentlyPlaying}" style="color: #a1a5ab;">volume_up</span>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                            <template v-if="Object.values(filteredPhoneticGroups).every(group => group.length === 0)">
                                <tr>
                                    <td colspan="3" style="margin-right: 18px;">
                                        <p class="rlms-text-center rlms-my-6">No phonetic symbols available <br> for the entered keyword.</p>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </BaseModal>
</template>

<script setup>
import { ref, computed } from 'vue';
import BaseModal from '../BaseModal.vue';
import { phoneticSymbolData } from '@/utils/assessmentGroups';

// Modal Title
const title = 'Phonetic Symbol Guide';

// Emits close/save events
const emit = defineEmits(['close', 'saved']);

// Controls modal visibility and search input
const showModal = ref(true);
const inputTag = ref('');
const audioInstance = ref(null);
const currentlyPlaying = ref(null);

// Handle modal close or save
const handleModal = (action = 'close') => {
    showModal.value = false;
    emit(action);
};

// Group data by phoneme for table layout
const groupedPhoneticData = computed(() => {
    const groups = {};
    for (const row of phoneticSymbolData) {
        if (!groups[row.phoneme]) {
            groups[row.phoneme] = [];
        }
        groups[row.phoneme].push(row);
    }
    return groups;
});

// Filter phonetic symbols and sounds (phoneme header is preserved)
const filteredPhoneticGroups = computed(() => {
    const keyword = inputTag.value.toLowerCase().trim();
    if (!keyword) return groupedPhoneticData.value;

    const result = {};
    for (const [phoneme, rows] of Object.entries(groupedPhoneticData.value)) {
        const filteredRows = rows.filter(row =>
            (row.symbol && row.symbol.toLowerCase().includes(keyword)) ||
            (row.sound && row.sound.toLowerCase().includes(keyword))
        );
        if (filteredRows.length) {
            result[phoneme] = filteredRows;
        }
    }
    return result;
});

const playSound = (audioPath, index) => {
    // Stop previous audio if needed
    if (audioInstance.value) {
        audioInstance.value.pause();
    }

    audioInstance.value = new Audio(audioPath);

    currentlyPlaying.value = index; // Highlight the button

    audioInstance.value.play();

    // Remove highlight when audio ends
    audioInstance.value.onended = () => {
        currentlyPlaying.value  = null;
    };
}
</script>
<style scoped>
.active {
    color: #0b74e6 !important;
}
</style>